// ============================================================================
// REQUESTS TEST PAGE - Integration Demo
// ============================================================================
// Demonstrates the integration of the Requests API with React UI

import React, { useState } from 'react';
import {
  useRequests,
  useMyRequests,
  useCreateRequest,
  useSubmitRequest,
  useDeleteRequest,
} from 'hooks/api';
import type { Request, CreateRequestRequest, RequestQueryParameters, ID } from 'api/types';
import { ApprovalDocumentTest } from '../components/modules/das/ticket-details/approval-document/ApprovalDocumentTest';

/**
 * Requests Test Page Component
 */
export const RequestsTestPage: React.FC = () => {
  // State for filters
  const [filters, setFilters] = useState<RequestQueryParameters>({
    page: 1,
    pageSize: 10,
    sortBy: 'SubmittedAt',
    sortOrder: 'desc',
  });

  // State for new request form
  const [newRequest, setNewRequest] = useState<CreateRequestRequest>({
    title: '',
    description: '',
    requestTypeId: '0e46a0f4-8e0f-4204-a92c-1e044fe7255b', // From your test data
    priority: 'Medium',
    budgetAmount: 0,
    requestValue: 0,
    currencyCode: 'USD',
    requiredByDate: '',
    metadata: '{}',
  });

  // Hooks
  const { items, pagination, loading, error, refetch } = useRequests(filters);
  const { data: myRequests, loading: loadingMy, refetch: refetchMyRequests } = useMyRequests();
  const { createRequest, loading: creating, error: createError } = useCreateRequest();
  const { submitRequest, loading: submitting } = useSubmitRequest();
  const { deleteRequest, loading: deleting } = useDeleteRequest();

  // Event Handlers
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clean up data before sending - remove empty strings
    const cleanedData = {
      ...newRequest,
      requiredByDate: newRequest.requiredByDate || undefined,
    };

    console.log('Submitting request with data:', cleanedData);

    try {
      const created = await createRequest(cleanedData, {
        onSuccess: (request) => {
          alert(`Request created successfully!\nRequest Number: ${request.requestNumber}\nID: ${request.id}`);
          refetch();
          refetchMyRequests();
          // Reset form
          setNewRequest({
            title: '',
            description: '',
            requestTypeId: '0e46a0f4-8e0f-4204-a92c-1e044fe7255b',
            priority: 'Medium',
            budgetAmount: 0,
            requestValue: 0,
            currencyCode: 'USD',
            requiredByDate: '',
            metadata: '{}',
          });
        },
        onError: (error: any) => {
          console.error('Full error object:', error);
          console.error('Error response:', error.response?.data);
          const backendErrors = error.response?.data?.errors || [];
          const backendMessage = error.response?.data?.message || error.message;
          const errorDetails = backendErrors.length > 0
            ? `\n\nValidation Errors:\n${backendErrors.join('\n')}`
            : '';
          alert(`Error creating request: ${backendMessage}${errorDetails}`);
        },
      });

      console.log('Created request:', created);
    } catch (err) {
      console.error('Failed to create request:', err);
    }
  };

  const handleSubmit = async (id: ID) => {
    if (!window.confirm('Are you sure you want to submit this request?')) return;

    try {
      await submitRequest(id, {
        onSuccess: (request) => {
          alert(`Request submitted successfully!\nStatus: ${request.currentStatus}`);
          refetch();
          refetchMyRequests();
        },
        onError: (error) => {
          alert(`Error submitting request: ${error.message}`);
        },
      });
    } catch (err) {
      console.error('Failed to submit request:', err);
    }
  };

  const handleDelete = async (id: ID) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      await deleteRequest(id, {
        onSuccess: () => {
          alert('Request deleted successfully!');
          refetch();
          refetchMyRequests();
        },
        onError: (error) => {
          alert(`Error deleting request: ${error.message}`);
        },
      });
    } catch (err) {
      console.error('Failed to delete request:', err);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '2rem', color: '#1e40af' }}>
        Requests API Integration - Test Page
      </h1>

      {/* Approval Document Test - DELETE AFTER TESTING */}
      <div style={{ marginBottom: '3rem', padding: '1.5rem', background: '#eff6ff', borderRadius: '0.5rem', border: '2px solid #3b82f6' }}>
        <h2 style={{ marginBottom: '0.5rem', color: '#1e40af' }}>Approval Document Preview Test</h2>
        <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.875rem' }}>
          Click the button below to preview the A4 approval certificate document with sample data.
        </p>
        <ApprovalDocumentTest />
      </div>

      {/* Create Request Form */}
      <div style={{ marginBottom: '3rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
        <h2 style={{ marginBottom: '1rem' }}>Create New Request</h2>
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Title <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={newRequest.title}
              onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
              placeholder="Enter request title"
              required
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Description
            </label>
            <textarea
              value={newRequest.description}
              onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
              placeholder="Enter request description"
              rows={3}
              maxLength={10000}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '4px',
              fontSize: '12px',
              color: (newRequest.description?.length || 0) > 9000 ? '#ef4444' : '#64748b'
            }}>
              {newRequest.description?.length || 0} / 10000
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Priority</label>
              <select
                value={newRequest.priority}
                onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value as any })}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Budget Amount</label>
              <input
                type="number"
                value={newRequest.budgetAmount}
                onChange={(e) => setNewRequest({ ...newRequest, budgetAmount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                min="0"
                step="0.01"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Request Value</label>
              <input
                type="number"
                value={newRequest.requestValue}
                onChange={(e) => setNewRequest({ ...newRequest, requestValue: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                min="0"
                step="0.01"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Required By Date</label>
            <input
              type="date"
              value={newRequest.requiredByDate}
              onChange={(e) => setNewRequest({ ...newRequest, requiredByDate: e.target.value ? `${e.target.value}T00:00:00Z` : '' })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem' }}
            />
          </div>

          {createError && (
            <div style={{ padding: '0.75rem', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '0.25rem', color: '#991b1b' }}>
              Error: {createError.message}
            </div>
          )}

          <button
            type="submit"
            disabled={creating}
            style={{
              padding: '0.75rem 1.5rem',
              background: creating ? '#94a3b8' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontWeight: 500,
              cursor: creating ? 'not-allowed' : 'pointer',
            }}
          >
            {creating ? 'Creating...' : 'Create Request'}
          </button>
        </form>
      </div>

      {/* All Requests List */}
      <div style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>All Requests</h2>
        {loading ? (
          <div>Loading requests...</div>
        ) : error ? (
          <div style={{ padding: '1rem', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '0.25rem', color: '#991b1b' }}>
            Error loading requests: {error.message}
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '2rem', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '0.375rem', textAlign: 'center', color: '#64748b' }}>
            No requests found
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Request Number</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Title</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Priority</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Value</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Submitted</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((request: Request) => (
                    <tr key={request.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.75rem', fontWeight: 500 }}>{request.requestNumber}</td>
                      <td style={{ padding: '0.75rem' }}>{request.title}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            background: request.currentStatus === 'Draft' ? '#fef3c7' : request.currentStatus === 'Submitted' ? '#dbeafe' : request.currentStatus === 'Approved' ? '#d1fae5' : '#fee2e2',
                            color: request.currentStatus === 'Draft' ? '#92400e' : request.currentStatus === 'Submitted' ? '#1e40af' : request.currentStatus === 'Approved' ? '#065f46' : '#991b1b',
                          }}
                        >
                          {request.currentStatus}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>{request.priority || '-'}</td>
                      <td style={{ padding: '0.75rem' }}>
                        {request.requestValue ? `${request.currencyCode} ${request.requestValue.toLocaleString()}` : '-'}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#64748b' }}>
                        {new Date(request.submittedAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {request.currentStatus === 'Draft' && (
                            <>
                              <button
                                onClick={() => handleSubmit(request.id)}
                                disabled={submitting}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                }}
                              >
                                Submit
                              </button>
                              <button
                                onClick={() => handleDelete(request.id)}
                                disabled={deleting}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
                    disabled={!pagination.hasPreviousPage}
                    style={{
                      padding: '0.5rem 1rem',
                      background: pagination.hasPreviousPage ? '#3b82f6' : '#cbd5e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
                    disabled={!pagination.hasNextPage}
                    style={{
                      padding: '0.5rem 1rem',
                      background: pagination.hasNextPage ? '#3b82f6' : '#cbd5e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* My Requests */}
      <div>
        <h2 style={{ marginBottom: '1rem' }}>My Requests</h2>
        {loadingMy ? (
          <div>Loading your requests...</div>
        ) : myRequests.length === 0 ? (
          <div style={{ padding: '2rem', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '0.375rem', textAlign: 'center', color: '#64748b' }}>
            You haven't created any requests yet
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {myRequests.map((request: Request) => (
              <div
                key={request.id}
                style={{
                  padding: '1rem',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{request.requestNumber}</div>
                <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>{request.title}</div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.75rem' }}>
                  <span
                    style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '0.25rem',
                      background: request.currentStatus === 'Draft' ? '#fef3c7' : '#dbeafe',
                      color: request.currentStatus === 'Draft' ? '#92400e' : '#1e40af',
                    }}
                  >
                    {request.currentStatus}
                  </span>
                  {request.priority && (
                    <span style={{ color: '#64748b' }}>{request.priority}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestsTestPage;
